import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLocation } from 'wouter';
import { ActionRow } from '../components/conta/rows.js';
import { PageShell } from '../components/stage/PageShell.js';
import { revokeDevice } from '../lib/api/personal.js';
import { askRecoveryKey, regenerateRecovery, useRecoveryState } from '../lib/matrix/recovery.js';
import { useMatrixClient } from '../lib/matrix/useMatrix.js';
import { useVerificationState, verifyOwnDevices } from '../lib/matrix/verification.js';
import { useSession } from '../lib/session.js';
import { ACCOUNT_KEY, useAccount } from '../lib/useAccount.js';
import '../components/ui/skeleton.css';
import {
  GlyphKey,
  GlyphLaptop,
  GlyphPhone,
  GlyphShieldCheck,
  GlyphUnlock,
} from '../components/ui/Glyphs.js';

const PLATFORM_LABEL = { android: 'Android', ios: 'iPhone', web: 'Navegador' } as const;

function when(iso: string) {
  const d = new Date(iso);
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  return `há ${days} dias`;
}

/** Aparelhos conectados à conta (Ajustes › Privacidade e segurança), no molde das páginas de ajuste. */
export function AparelhosPage() {
  const qc = useQueryClient();
  const [, navigate] = useLocation();
  const accountQ = useAccount();
  const session = useSession();
  const [busy, setBusy] = useState<string | null>(null);
  const { client, status } = useMatrixClient();
  const recovery = useRecoveryState();
  const verification = useVerificationState();
  const devices = accountQ.data?.devices ?? [];
  const messagingReady = status === 'ready' && !!client;

  async function signOut(publicId: string) {
    setBusy(publicId);
    try {
      await revokeDevice(publicId);
      await qc.invalidateQueries({ queryKey: ACCOUNT_KEY });
    } catch {
      /* silencioso */
    } finally {
      setBusy(null);
    }
  }

  return (
    <PageShell
      kicker="Aparelhos"
      title="Onde você está conectado"
      lead="Saia de um aparelho que não é mais seu."
      onBack={() => navigate('/ajustes')}
      backLabel="Voltar para Ajustes"
    >
      <section className="pgs-section sk-cascade" aria-label="Aparelhos">
        {devices.map((device) => {
          const current = device.publicId === session?.device.publicId;
          return (
            <div className="pgs-row ct-row" key={device.publicId}>
              <span className="pgs-ic">
                {device.platform === 'web' ? <GlyphLaptop /> : <GlyphPhone />}
              </span>
              <span className="pgs-rowt">
                <b>
                  {device.label || PLATFORM_LABEL[device.platform]}
                  {current ? ' · este aparelho' : ''}
                </b>
                <small>{current ? 'Em uso agora' : `Visto ${when(device.lastSeenAt)}`}</small>
              </span>
              {!current && (
                <button
                  type="button"
                  className="pgs-btn small"
                  onClick={() => void signOut(device.publicId)}
                  disabled={busy === device.publicId}
                >
                  {busy === device.publicId ? 'Saindo…' : 'Sair'}
                </button>
              )}
            </div>
          );
        })}
        <p className="pgs-note">
          Cada entrada com código cria um aparelho. Sair daqui encerra a sessão dele na hora.
        </p>
      </section>

      {messagingReady && (
        <section className="pgs-section">
          <h2 className="pgs-h2">Proteção das conversas</h2>
          {devices.length > 1 && (
            <ActionRow
              onClick={() => client && void verifyOwnDevices(client)}
              icon={<GlyphShieldCheck />}
              title="Confirmar outro aparelho"
              hint="Os dois comparam sete desenhos e passam a se reconhecer."
              disabled={verification.busy || verification.stage !== 'idle'}
            />
          )}
          {recovery.needsUnlock && (
            <ActionRow
              onClick={askRecoveryKey}
              icon={<GlyphUnlock />}
              title="Destravar o histórico aqui"
              hint="Digite a sua chave para ver as conversas antigas neste aparelho."
            />
          )}
          <ActionRow
            onClick={() => client && void regenerateRecovery(client)}
            icon={<GlyphKey />}
            title={recovery.busy ? 'Criando a chave nova…' : 'Nova chave de recuperação'}
            hint="Perdeu a antiga? Crio outra e mostro uma única vez."
            disabled={recovery.busy}
          />
          {recovery.error && recovery.phase === 'ready' && (
            <p className="pgs-note" role="alert">
              {recovery.error}
            </p>
          )}
        </section>
      )}
    </PageShell>
  );
}
